<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Ecommerce</h2>
        <p class="text-muted mb-0">
          Nombre, logo, número de WhatsApp y modo de entrega que usa el storefront público.
        </p>
      </div>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'MaintenanceHome' }">
        Volver a Mantenimiento
      </router-link>
    </header>

    <div v-if="!canManageEcommerce" class="alert alert-warning mb-0">
      Esta sección está disponible sólo para usuarios con el permiso <code>gestionar_ecommerce</code>.
    </div>

    <template v-else>
      <div class="card shadow-sm">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Nombre fantasía del ecommerce</label>
              <input
                v-model="form.nombre_comercial"
                class="form-control"
                type="text"
                placeholder="Se usa el nombre comercial fiscal si se deja vacío"
              />
              <div class="form-text">
                Opcional — sobreescribe el nombre mostrado en el storefront cuando es distinto del nombre comercial fiscal.
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Número de WhatsApp del comercio</label>
              <input
                v-model="form.whatsapp_numero"
                class="form-control"
                type="text"
                placeholder="+595981000000"
              />
              <div class="form-text">
                Formato internacional completo — es el número al que llegan los pedidos confirmados por el storefront.
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Modo de entrega</label>
              <select v-model="form.envio_modo" class="form-select">
                <option value="SIN_ENVIO">Solo retiro en el local</option>
                <option value="CON_ENVIO">Solo envío a domicilio</option>
                <option value="AMBOS">Envío y retiro</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Logo del ecommerce</label>
              <input
                class="form-control"
                type="file"
                accept="image/svg+xml,image/png,image/jpeg,image/webp"
                @change="handleLogoFile"
              />
              <div class="form-text">
                Opcional — sobreescribe el logo mostrado en el storefront cuando es distinto del logo comercial fiscal.
              </div>
            </div>
            <div v-if="logoPreviewUrl || form.logo_url" class="col-12">
              <div class="branding-preview">
                <img :src="logoPreviewUrl || form.logo_url" alt="Logo del ecommerce" />
                <div>
                  <div class="fw-semibold">{{ form.nombre_comercial || 'Nombre del ecommerce' }}</div>
                  <div class="text-muted small">Preview de identidad del storefront</div>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Ícono de la pestaña (favicon)</label>
              <input
                class="form-control"
                type="file"
                accept="image/svg+xml,image/png,image/jpeg,image/webp"
                @change="handleFaviconFile"
              />
              <div class="form-text">
                Opcional — si no subís uno, se reusa el logo de arriba como ícono de la pestaña.
                Un ícono cuadrado y simple se ve mejor reducido a favicon que un logo ancho.
              </div>
            </div>
            <div v-if="faviconPreviewUrl || form.favicon_url" class="col-12">
              <div class="branding-preview">
                <img :src="faviconPreviewUrl || form.favicon_url" alt="Favicon del ecommerce" />
                <div>
                  <div class="fw-semibold">Ícono de la pestaña</div>
                  <div class="text-muted small">Así se ve en la pestaña del navegador</div>
                </div>
              </div>
            </div>
            <div class="col-12">
              <div class="alert alert-secondary mb-0">
                <div v-if="locales.length">
                  Locales activos para retiro: <strong>{{ locales.map((local) => local.nombre_establecimiento).join(', ') }}</strong>
                </div>
                <div v-else>Todavía no hay locales activos para retiro.</div>
                Dirección y horario se editan por local desde
                <router-link :to="{ name: 'MaintenanceFiscalEstablishments' }">Fiscal &gt; Establecimientos</router-link>.
              </div>
            </div>
          </div>
        </div>
        <div class="card-footer d-flex justify-content-end gap-2">
          <button class="btn btn-dark btn-sm" :disabled="saving" @click="save">
            {{ saving ? 'Guardando...' : 'Guardar cambios' }}
          </button>
        </div>
      </div>

      <!-- SPF-2A-001: apariencia del storefront (theming básico). Los colores
           NULL significan "usar el default del diseño" — por eso "Restaurar
           default" manda null en vez de un hex concreto. -->
      <div class="card shadow-sm">
        <div class="card-body">
          <h3 class="h6 mb-1">Apariencia del storefront</h3>
          <p class="text-muted small mb-3">
            Colores y textos que usa tu tienda online. Los cambios se ven en el storefront
            sin necesidad de volver a desplegarlo.
          </p>
          <div class="row g-3">
            <div v-for="color in colorFields" :key="color.key" class="col-md-4">
              <label class="form-label">{{ color.label }}</label>
              <div class="d-flex align-items-center gap-2">
                <input
                  v-model="form[color.key]"
                  class="form-control form-control-color"
                  type="color"
                  :title="color.label"
                />
                <code class="small text-muted">{{ form[color.key] }}</code>
                <button
                  v-if="!usaDefault(color.key)"
                  class="btn btn-outline-secondary btn-sm ms-auto"
                  type="button"
                  @click="restaurarDefault(color.key)"
                >
                  Restaurar
                </button>
                <span v-else class="badge text-bg-light ms-auto">Default</span>
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Eslogan de portada</label>
              <input
                v-model="form.eslogan"
                class="form-control"
                type="text"
                maxlength="200"
                placeholder="Opcional — subtítulo bajo el nombre del comercio"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label">Texto del pie de página</label>
              <input
                v-model="form.texto_footer"
                class="form-control"
                type="text"
                maxlength="300"
                placeholder="Opcional — aparece al final del storefront"
              />
            </div>
          </div>
        </div>
        <div class="card-footer d-flex justify-content-end gap-2">
          <button class="btn btn-dark btn-sm" :disabled="saving" @click="save">
            {{ saving ? 'Guardando...' : 'Guardar cambios' }}
          </button>
        </div>
      </div>

      <!-- BIC-7B-003: las imágenes del catálogo alimentan el storefront; el
           cliente ecommerce debe poder llegar a esa herramienta desde acá. -->
      <div class="card shadow-sm">
        <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h3 class="h6 mb-1">Imágenes de catálogo</h3>
            <p class="text-muted small mb-0">
              Auditoría y carga automatizada (con IA) de imágenes para los productos sin foto —
              son las imágenes que muestra tu tienda online.
            </p>
          </div>
          <router-link class="btn btn-dark btn-sm" :to="{ name: 'EcommerceImagenes' }">
            Abrir imágenes
          </router-link>
        </div>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { hasPermission } from '@/services/authSessionService'
import ecommerceConfigService from '@/services/ecommerceConfigService'

const canManageEcommerce = computed(() => hasPermission('gestionar_ecommerce'))
const saving = ref(false)

// Defaults del diseño del storefront: mismos valores que resuelve el backend
// cuando la columna está NULL (storefrontResolverService.TEMA_DEFAULT). Acá
// sirven para que el <input type="color"> muestre algo — un color vacío no
// existe en HTML — y para saber si el usuario está sobre el default.
const COLORES_DEFAULT = Object.freeze({
  color_primario: '#07a7e1',
  color_acento: '#057aa5',
  color_fondo: '#ffffff',
})

const colorFields = [
  { key: 'color_primario', label: 'Color primario' },
  { key: 'color_acento', label: 'Color de acento' },
  { key: 'color_fondo', label: 'Color de fondo' },
]

const form = reactive({
  nombre_comercial: '',
  whatsapp_numero: '',
  envio_modo: 'SIN_ENVIO',
  logo_url: '',
  favicon_url: '',
  color_primario: COLORES_DEFAULT.color_primario,
  color_acento: COLORES_DEFAULT.color_acento,
  color_fondo: COLORES_DEFAULT.color_fondo,
  eslogan: '',
  texto_footer: '',
})

const usaDefault = (key) => (form[key] || '').toLowerCase() === COLORES_DEFAULT[key].toLowerCase()

function restaurarDefault(key) {
  form[key] = COLORES_DEFAULT[key]
}
const locales = ref([])
const pendingLogoFile = ref(null)
const logoPreviewUrl = ref('')
const pendingFaviconFile = ref(null)
const faviconPreviewUrl = ref('')

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function handleLogoFile(event) {
  const file = event?.target?.files?.[0] || null
  pendingLogoFile.value = file
  logoPreviewUrl.value = file ? URL.createObjectURL(file) : ''
}

function handleFaviconFile(event) {
  const file = event?.target?.files?.[0] || null
  pendingFaviconFile.value = file
  faviconPreviewUrl.value = file ? URL.createObjectURL(file) : ''
}

async function load() {
  if (!canManageEcommerce.value) return
  try {
    const data = await ecommerceConfigService.get()
    form.nombre_comercial = data.nombre_comercial || ''
    form.whatsapp_numero = data.whatsapp_numero || ''
    form.envio_modo = data.envio_modo || 'SIN_ENVIO'
    form.logo_url = data.logo_url || ''
    form.favicon_url = data.favicon_url || ''
    // Columna NULL = sin override: se muestra el default del diseño
    // (SPF-2A-002).
    for (const { key } of colorFields) {
      form[key] = data[key] || COLORES_DEFAULT[key]
    }
    form.eslogan = data.eslogan || ''
    form.texto_footer = data.texto_footer || ''
    locales.value = data.locales || []
  } catch (error) {
    console.error('Error cargando configuración de ecommerce', error)
  }
}

async function save() {
  saving.value = true
  try {
    if (pendingLogoFile.value) {
      const upload = await ecommerceConfigService.uploadLogo({
        filename: pendingLogoFile.value.name,
        data_url: await fileToDataUrl(pendingLogoFile.value),
      })
      form.logo_url = upload?.logo_url || form.logo_url
      pendingLogoFile.value = null
    }
    if (pendingFaviconFile.value) {
      const upload = await ecommerceConfigService.uploadFavicon({
        filename: pendingFaviconFile.value.name,
        data_url: await fileToDataUrl(pendingFaviconFile.value),
      })
      form.favicon_url = upload?.favicon_url || form.favicon_url
      pendingFaviconFile.value = null
    }
    await ecommerceConfigService.update({
      nombre_comercial: form.nombre_comercial,
      whatsapp_numero: form.whatsapp_numero,
      envio_modo: form.envio_modo,
      // Un color igual al default se guarda como `null` (limpia el override):
      // así el storefront sigue el diseño base en vez de quedar clavado a un
      // hex que después no acompañaría un cambio de diseño.
      ...Object.fromEntries(
        colorFields.map(({ key }) => [key, usaDefault(key) ? null : form[key]])
      ),
      eslogan: form.eslogan?.trim() || null,
      texto_footer: form.texto_footer?.trim() || null,
    })
    window.alert('Configuración de ecommerce guardada')
    logoPreviewUrl.value = ''
    faviconPreviewUrl.value = ''
    await load()
  } catch (error) {
    console.error('Error guardando configuración de ecommerce', error)
    const detalle = error?.userMessage || error?.response?.data?.error
    window.alert(
      detalle
        ? `No se pudo guardar la configuración de ecommerce: ${detalle}`
        : 'No se pudo guardar la configuración de ecommerce'
    )
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.branding-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
}

.branding-preview img {
  width: 64px;
  height: 64px;
  object-fit: contain;
}
</style>
